<script lang="ts" setup>
    import { computed, inject, ref, watch } from "vue";
    import { useForm } from "@/helpers/composables/useForm";
    import { object, string } from 'yup'
    import { route } from "ziggy-js";
    import RothcoCta from "./RothcoCta.vue";
    import InputMask from "primevue/inputmask";
    import { useAddressValidation } from "@/helpers/composables/useAddressValidation";
    import {useSettingsStore } from '@/store/settings'
    import CloseIcon from "@icons/closeIcon.svg";
    const dialogRef = inject("dialogRef");
    let { setNotificationMessage } = useSettingsStore();
    function closeDialog(){
        dialogRef.value.close();
    }
    let form = useForm({
        useInertia: false,
        schema: object({
            company_name: string().required('Company Name is required'),
            email_requesting_change: string().email("Email is invalid").notRequired(),
            phone_requesting_change: string().notRequired(),
            reason: string().required('Reason for change is required'),
            ship_to_name: string().required('Shipping name is required'),
            ship_to_address1: string().required('Street 1 is required'),
            ship_to_address2: string(),
            ship_to_address3: string(),
            ship_to_city: string().optional().test(
                'is-not-empty',
                'City is required',
                (value) => {
                    if(form.ship_to_country == 'USA' || form.ship_to_country == 'CAN'){
                        return  value !== undefined && value !== '' && value !== null;
                    }
                    return true;
                }
            ),
            ship_to_region: string().optional().test(
                'is-not-empty',
                'State is required',
                (value) => {
                    if(form.ship_to_country == 'USA' || form.ship_to_country == 'CAN'){
                        return  value !== undefined && value !== '' && value !== null;
                    }
                    return true;
                }
            ),
            ship_to_zip: string().optional()
            .test(
                'is-not-empty',
                'Zip is required',
                (value) => {
                    if(form.ship_to_country == 'USA' || form.ship_to_country == 'CAN'){
                        return  value !== undefined && value !== '' && value !== null;
                    }
                    return true;
                }
            )
            .test({
                name: 'ship_to_zip',
                message: 'Invalid zip code',
                test: (value) => {
                    if(form.ship_to_country == 'USA' || form.ship_to_country == 'CAN'){
                        return /^(?:\d{5}(?:-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d|[A-Z]{1,2}\d[A-Z\d]? \d[A-Z]{2}|GIR 0AA)$/.test(
                            value
                        );
                    }
                    return true;
                }
            }),
            ship_to_country: string().required('Country is required'),
        })
    })

    let { reactiveAddress, isValid, isDifferent, useSuggestedAddress } = useAddressValidation({
        street1: form.ship_to_address1,
        street2: form.ship_to_address2,
        city: form.ship_to_city,
        state: form.ship_to_region,
        postal_code: form.ship_to_zip,
        country: form.ship_to_country
    }, true);

    let fullAddress = computed(() => {
        return (
            form.ship_to_address1 != "" &&
            form.ship_to_address1 != null &&
            form.ship_to_city != "" &&
            form.ship_to_city != null &&
            form.ship_to_region != "" &&
            form.ship_to_region != null &&
            form.ship_to_zip != "" &&
            form.ship_to_zip != null &&
            form.ship_to_country != "" &&
            form.ship_to_country != null
        );
    });


    function changeAddress() {
        let addy = useSuggestedAddress();

        form.ship_to_address1 = addy.street1;
        form.ship_to_address2 = addy.street2;

        form.ship_to_city = addy.city;
        form.ship_to_region = addy.state;
        form.ship_to_zip = addy.postal_code;
        form.ship_to_country = addy.country;
    }


    watch(form, (newVal)=>{
        const diffShippingStreet1 = reactiveAddress.value.street1 != newVal.ship_to_address1;
        const diffShippingStreet2 = reactiveAddress.value.street2 != newVal.ship_to_address2;
        const diffShippingCity = reactiveAddress.value.city != newVal.ship_to_city;
        const diffShippingState = reactiveAddress.value.state != newVal.ship_to_region;
        const diffShippingZip = reactiveAddress.value.postal_code != newVal.ship_to_zip;
        const diffShippingCountry = reactiveAddress.value.country != newVal.ship_to_country;
        if (diffShippingStreet1 && fullAddress.value) {
            reactiveAddress.value.street1 = newVal.ship_to_address1;
        }
        if(diffShippingStreet2 && fullAddress.value) {
            reactiveAddress.value.street2 = newVal.ship_to_address2;
        }
        if(diffShippingCity && fullAddress.value){
            reactiveAddress.value.city = newVal.ship_to_city;
        }
        if(diffShippingState && fullAddress.value){
            reactiveAddress.value.state = newVal.ship_to_region;
        }
        if(diffShippingZip && fullAddress.value){
            reactiveAddress.value.postal_code = newVal.ship_to_zip;
        }
        if(diffShippingCountry && fullAddress.value){
            reactiveAddress.value.country = newVal.ship_to_country;
        }
    }, {deep: true});

    let canSubmitForm = computed(()=>!Object.keys(form.errors)?.length);
    async function submit(){
        if(form.processing) return;

        await form.validate({all: true});
        
        if(canSubmitForm.value){
            form.post(route('account.addShipTo'),
                form.transform((data)=>{
                    let {errors, processing, ...payload} = data;
                    return {
                        ship_to: {
                            ...payload
                        }
                    }
                }),{
                    afterFetch(ctx){
                        setNotificationMessage({
                            message: `Request sent!`,
                            type: 'success'
                        })
                        closeDialog();
                    }
                }
            );
        }
    }
    let formattedCountries = computed(()=>{
        return dialogRef?.value?.data?.countries?.map((country)=>{
            return {
                value: country.name,
                key: country.alpha3code
            }
        }) ?? [];
    })
    let selectedCountry = computed({
        get(){
            return formattedCountries.value.find(currentCountry => currentCountry.key == form.ship_to_country) ?? {key: '', value: ''};
        },
        set(value){
            form.ship_to_country = value.key;
        }
    })
    const requireStateCityZip = computed(()=>{
        return !!form.ship_to_country && ['USA','CAN'].includes(form.ship_to_country);
    })
    const CANADA_STATE_CODES = new Set(['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT']);
    let formattedRegions = computed(()=>{
        const regions = dialogRef?.value?.data?.regions ?? [];
        const countryRegionMap = {
            'CAN': regions
                .filter(region => CANADA_STATE_CODES.has(region.code) && region.code !== 'PR')
                .map(region => ({ key: region.code, value: region.name })),
            'PRI': [{ key: 'PR', value: 'Puerto Rico' }],
            'USA': regions
                .filter(region => !CANADA_STATE_CODES.has(region.code))
                .map(region => ({ key: region.code, value: region.name }))
        };
        return countryRegionMap[form.ship_to_country] ?? null;
    })
    let selectedRegion = computed({
        get(){
            return formattedRegions.value.find(currentRegion => currentRegion.key == form.ship_to_region) ?? {key: '', value: ''};
        },
        set(newVal){
            form.ship_to_region = newVal.key;
        }
    })
    watch(() => form.ship_to_country, (newCountryValue, oldCountryValue) => {
        if (!['USA', 'CAN', 'PRI'].includes(newCountryValue) && ['USA', 'CAN', 'PRI'].includes(oldCountryValue)) {
            form.ship_to_region = '';
            return;
        }
        if (['USA', 'CAN', 'PRI'].includes(newCountryValue)) {
            let state = form.ship_to_region?.toLowerCase();
            let availableStates = formattedRegions.value || [];

            let matchedState = availableStates.find(
                s => s.value?.toLowerCase() === state || s.key?.toLowerCase() === state
            );
            form.ship_to_region = matchedState ? matchedState.key : '';
        }
    });
</script>
<template>
    <div>
        <div class="modal-overlay active" @click="closeDialog()"></div>
        <div class="modal">
            <div class="modal__header">
                <h2 id="rothcoModalTitle" class="headline-s">Request New Shipping Address</h2>
                <CloseIcon @click="closeDialog()" class="modal__header--close" alt="Close Modal" aria-label="Close" />
            </div>
            <form @submit.prevent="submit" class="modal__body flex-column">
                <div class="flex-row justify-between">
                    <!-- make input for company name with errors-->
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="company_name" v-model="form.company_name" placeholder=" " :class="{
                                'invalid-state-input': form.errors.company_name != null
                            }">
                            <label for="company_name">
                                <p class="paragraph-default">Company Name *</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.company_name }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="reason" v-model="form.reason" placeholder=" " :class="{
                                'invalid-state-input': form.errors.reason != null
                            }">
                            <label for="reason">
                                <p class="paragraph-default">Reason *</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.reason }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex-row justify-between">
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="email" v-model="form.email_requesting_change" placeholder=" " :class="{
                                'invalid-state-input': form.errors.email_requesting_change != null
                            }">
                            <label for="email">
                                <p class="paragraph-default">Email</p>
                            </label>
                        </div>
                        <div class="input-folder--support" v-if="form.errors.email_requesting_change">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.email_requesting_change }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <InputMask
                                id="phone" v-model="form.phone_requesting_change"
                                mask="(999) 999-9999"
                                placeholder=" "
                                fluid
                                :class="{
                                    'invalid-state-input': form.errors.phone_requesting_change != null
                                }"
                            />
                            <label for="phone">
                                <p>Phone</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state" v-if="form.errors.phone_requesting_change">
                                {{ form.errors.phone_requesting_change }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex-row justify-between">
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_name" v-model="form.ship_to_name" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_name != null
                            }">
                            <label for="ship_to_name">
                                <p class="paragraph-default">Shipping Name *</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_name }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_address1" v-model="form.ship_to_address1" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_address1 != null
                            }">
                            <label for="ship_to_address1">
                                <p class="paragraph-default">Shipping Street 1 *</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_address1 }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex-row justify-between">
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_address2" v-model="form.ship_to_address2" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_address2 != null
                            }">
                            <label for="ship_to_address2">
                                <p class="paragraph-default">Shipping Street 2</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_address2 }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_address3" v-model="form.ship_to_address3" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_address3 != null
                            }">
                            <label for="ship_to_address3">
                                <p class="paragraph-default">Shipping Street 3</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_address3 }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex-row justify-between">
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_city" v-model="form.ship_to_city" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_city != null
                            }">
                            <label for="ship_to_city">
                                <p class="paragraph-default">Shipping City {{ requireStateCityZip ? '*' : '' }}</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_city }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div v-if="formattedRegions">
                            <custom-select 
                                v-model="selectedRegion"
                                :label="`Shipping State ${requireStateCityZip ? '*' : ''}`"
                                name="ship_to_region"
                                id="ship_to_region"
                                aria-label="Enter State/Province"
                                :options="formattedRegions"
                            />
                        </div>
                        <div v-if="!formattedRegions" class="input-folder--input">
                            <input type="text" id="ship_to_region" v-model="form.ship_to_region" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_region != null
                            }">
                            <label for="ship_to_region">
                                <p class="paragraph-default">Shipping State {{ requireStateCityZip ? '*' : '' }}</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_region }}
                            </p>
                        </div>
                    </div>
                </div>
                <div class="flex-row justify-between">
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <input type="text" id="ship_to_zip" v-model="form.ship_to_zip" placeholder=" " :class="{
                                'invalid-state-input': form.errors.ship_to_zip != null
                            }">
                            <label for="ship_to_zip">
                                <p class="paragraph-default">Shipping Zip {{ requireStateCityZip ? '*' : '' }}</p>
                            </label>
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_zip }}
                            </p>
                        </div>
                    </div>
                    <div class="input-folder">
                        <div class="input-folder--input">
                            <custom-select
                                v-model="selectedCountry"
                                label="Shipping Country *"
                                name="ship_to_country" 
                                id="ship_to_country" 
                                aria-label="Enter Country"
                                :options="formattedCountries"
                            />
                        </div>
                        <div class="input-folder--support">
                            <p class="input-support-text invalid-state">
                                {{ form.errors.ship_to_country }}
                            </p>
                        </div>
                    </div>  
                </div>

                <p v-show="!isValid && fullAddress && ['US', 'USA', 'PR', 'PRI'].includes(form.ship_to_country)">The address you entered is invalid. Please enter a valid address.</p>
                <p v-show="isDifferent && fullAddress">
                    The address you entered is different from the suggested address.
                    <span style="text-decoration: underline; cursor: pointer;" @click="changeAddress"
                        >Would you like to use the suggested address?</span
                    >
                </p>

                <div class="flex-row justify-between justify-end">
                    <RothcoCta
                        :disabled="form.processing"
                        class="primary"
                        @click.prevent="submit"
                        type="submit"
                        id="submit"
                    >
                    Submit
                </RothcoCta>

                </div>
            </form>
        </div>
    </div>
</template>